import * as GC from '@grapecity/spread-sheets';
import { spreadsheetConfig } from '@shared/spreadsheet/spreadsheet-config';
import { fromEvent, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { resumeAll } from './spread/resume-all';
import { suspendAll } from './spread/suspend-all';

// ! access to the clipboard vary significantly between browsers.
// + Info: https://developer.mozilla.org/en-US/docs/Web/API/Clipboard

export type ClipboardEventName = 'copy' | 'cut' | 'paste';
const warnMsg = `This clipboard action is not allowed by the user`;

export const clipboardCopy = async (str: string = ''): Promise<void> => {
  // Trying direct copy
  const alternatives = [
    async () => {
      // Force copy without asking for permission
      const el = document.createElement('textarea');
      el.value = str;
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      return await el.value;
    },
    async () => {
      if (window && window['clipboardData']) {
        window['clipboardData'].setData('Text', str);
      } else if (navigator && navigator['clipboard'] && navigator['permissions']) {
        const getPermissionStatus = async () => await navigator['permissions'].query(<any>{ name: 'clipboard-write' });

        // Make a request to ask for permission
        // Write in the clipboard
        const copied = await navigator['clipboard'].writeText(str).catch(err => {
          console.warn(warnMsg);
        });

        if (getPermissionStatus['state'] === 'granted') {
          return copied;
        } else {
          // Try for a second time once the permission has been prompt
          await navigator['clipboard'].writeText(str).catch(err => {
            console.warn(warnMsg);
          });
        }
      }
    },
  ];

  for (let i = 0; i < alternatives.length; i++) {
    alternatives[i]();
  }
};

export const displayClipboardReadBlockDialog = async () => {
  // Sweet Alert (user-friendly)
  document.dispatchEvent(new Event('isRightClickSwalOpen'));

  const close$ = new Subject();
  const escapeClose$ = fromEvent(document.querySelector('.cdk-overlay-container'), 'keydown')
    .pipe(
      takeUntil(close$),
      filter((keyEvent: KeyboardEvent) => keyEvent.key === 'Escape' || keyEvent.code === 'Escape')
    )
    .subscribe(e => {
      Swal.close();
      close$.next();
      close$.complete();
      document.dispatchEvent(new Event('isRightClickSwalClosed'));
    });

  return await Swal.fire({
    title: 'Clipboard read access denied',
    html: `Your browser cannot access the clipboard, so please use these shortcuts:
<ul style="list-style-type: none; text-align: center; margin: 0; padding: 0; margin-top: 0.5em;">
<strong>
<li>Cut = Ctrl + X</li>
<li>Copy = Ctrl + C</li>
<li>Paste = Ctrl + V</li>
</strong>
</ul>`,
    //  text: `You cannot right-click paste because your browser is blocking the permission to read from your Clipboard.
    type: 'warning',
    showCancelButton: false,
    confirmButtonText: 'OK',
    reverseButtons: false,
    allowEscapeKey: true,
  } as SweetAlertOptions).then(async result => {
    // clipboardText = await navigator['clipboard'].readText();
    // console.log({ result });
    return result.value;
  });
};

export const getClipboardData = async (): Promise<string> => {
  if (window && window['clipboardData']) {
    try {
      const clipboardText = window['clipboardData'].getData('Text');
      return clipboardText;
    } catch (err) {
      // console.warn(warnMsg);
    }
  } else if (navigator && navigator['clipboard'] && navigator['permissions']) {
    const getPermissionStatus = async () => await navigator['permissions'].query(<any>{ name: 'clipboard-read' });

    // Ask for permission trying to read from clipboard
    let clipboardText = await navigator['clipboard'].readText().catch(err => undefined);

    if (clipboardText === undefined) {
      displayClipboardReadBlockDialog();
    } else if (getPermissionStatus['state'] !== 'denied') {
      // Read from clipboard once the permission has been granted
      clipboardText = await navigator['clipboard'].readText();
    }

    return clipboardText;
  } else {
    try {
      let result = '';
      let el: any = document.getElementById('clipboard-sandbox');
      if (!el) {
        el = document.createElement('textarea');
        el.setAttribute('id', 'clipboard-sandbox');
        el.setAttribute('contentEditable', 'true');
        el.setAttribute('isContentEditable', 'true');
        el.setAttribute('value', '');
      } else {
        el.value = '';
      }
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('paste');
      const pastedValue = el.value as string;
      result = pastedValue;
      return result;
    } catch (err) {
      console.warn(warnMsg);
    }
  }
  return;
};

export const getPastedDataMatrix = pasteText => {
  const matrix = pasteText
    // split by rows
    .split(spreadsheetConfig.excelTextSeparators.row)
    // split by columns
    .map(elem =>
      elem
        // patch for the 1-column pasting edge case
        .split(spreadsheetConfig.excelTextSeparators.col)
        .map(x => x.trim())
    );

  // if (
  //   pasteText.length > 0 &&
  //   matrix.length > 0 &&
  //   pasteText[pasteText.length - 1] === spreadsheetConfig.excelTextSeparators.row &&
  //   matrix.every(row => row.length === 1)
  // ) {
  //   // Removing the last special character
  //   console.log('Removing the last special character');
  //   matrix.pop();
  // }

  return matrix;
};

export const paste = (sheet, selection = null, pastedText: string /* ***, event*/): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    if (typeof pastedText === 'string') {
      const matrix = getPastedDataMatrix(pastedText);

      if (pastedText.length > 0 && pastedText[pastedText.length - 1] === spreadsheetConfig.excelTextSeparators.row && matrix.length > 0) {
        // Removing the last special character
        // console.log('Removing the last special character'+
        // 'to avoid undesired behaviours like removing content of adjacent non-selected cells');
        matrix.pop();
      }

      if (matrix && matrix[0]) {
        // Set the cell values
        // (from left to right && from top to bottom)
        // -------------------
        // ! Modifying it can introduce defects in multiple pages
        const necessaryDelay = 100;
        setTimeout(
          () => {
            // ! Don't remove the timeout:
            // Necessary to move the callback outside of the event loop
            // and delay his execution until the event ends
            suspendAll(sheet);
            // console.log({ event });
            if (matrix.length === 1 && matrix[0].length === 1) {
              // Unique value to paste to fill the selection
              const fillPasteValue = matrix[0][0];
              const { row: selRow, col: selCol, rowCount: selRowCount, colCount: selColCount } = selection;
              for (let r = selRow; r < selRow + selRowCount; r++) {
                for (let c = selCol; c < selCol + selColCount; c++) {
                  sheet.getCell(r, c).value(fillPasteValue);
                }
              }
            } else {
              // Multiple values to paste
              matrix.forEach((_row, _rindex) => {
                _row.forEach((cellValue, _cindex) => {
                  const row = selection.row + _rindex,
                    col = selection.col + _cindex;
                  sheet.getCell(row, col).value(cellValue);
                });
              });

              (sheet as GC.Spread.Sheets.Worksheet).setSelection(selection.row, selection.col, matrix.length, matrix[0].length);
            }
            resumeAll(sheet);
            resolve(true);
          },
          // ! Delay necessary
          // to wait until the parent events (Pasting and Pasted has end) and avoid event.preventDefault()          necessaryDelay
          necessaryDelay
        );
      } else {
        resolve(false);
      }
    }
  });
};

// This sould be at the start of ClipboardPasting events of every page
export const clipboardClearOnCut = (info, sheet: GC.Spread.Sheets.Worksheet): void => {
  // ! Commented for beta while paste is disabled per fix 3185
  // if (info.pasteOption === 0) {
  //   sheet.clearSelection();
  //   // ! Do not remove Timeout
  //   // -- It delays the execution until the event ends
  //   setTimeout(() => {
  //     let cleared = false;
  //     if (navigator && navigator['clipboard']) {
  //       clipboardCopy();
  //       cleared = true;
  //     } else if (window && window['clipboardData']) {
  //       window['clipboardData'].clearData('Text');
  //       cleared = true;
  //     }
  //     if (cleared) {
  //       console.log('clipboard cleared');
  //     }
  //   }, 500);
  // }
};
